import type { Dayjs } from 'dayjs';

/**
 * Calendar-date helpers for everything the app sends to the server.
 *
 * A calendar date has no timezone. It is sent as a plain 'YYYY-MM-DD' string
 * and rendered from that string, so there is never an instant for a browser
 * zone to shift. This is the frontend half of the backend's CalendarDate
 * contract — the frontend needs no equivalent class, because it never does
 * arithmetic on stored dates, only on picker values, which are local by design.
 */

/** First day of the month `d` falls in, as 'YYYY-MM-DD'. */
export const toIsoMonth = (d: Dayjs): string =>
  d.startOf('month').format('YYYY-MM-DD');

/** Last day of the month `d` falls in, as 'YYYY-MM-DD'. */
export const toIsoMonthEnd = (d: Dayjs): string =>
  d.endOf('month').format('YYYY-MM-DD');

/** The calendar day a picker value represents, as 'YYYY-MM-DD'. */
export const toIsoDate = (d: Dayjs | Date): string => {
  // A Dayjs is formatted in its OWN mode. Pickers seeded with `dayjs.utc(...)`
  // hand back a UTC-mode value, and converting it to a Date first would read
  // the local day — the previous one for anyone behind UTC. A bare Date has no
  // mode, so the local day is the day the picker showed.
  if (!(d instanceof Date)) return d.format('YYYY-MM-DD');
  return [
    String(d.getFullYear()).padStart(4, '0'),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
};

/**
 * Renders a stored calendar date without consulting any timezone.
 *
 * Accepts either the 'YYYY-MM-DD' the server will send once the Date scalar is
 * split, or the UTC-midnight instant it sends today — both describe the same
 * calendar day, and neither is resolved in the ambient zone.
 */
export const formatCalendarDate = (
  value: string | Date | null | undefined,
  separator = '-',
): string => {
  if (!value) return '';
  const iso =
    typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return '';
  return [day, month, year].join(separator);
};
